/**
 * Cross-app sign-in with recruiter.vriti.ai (and the other *.vriti.ai frontends).
 *
 * The recruiter app publishes its access token on the shared parent domain under an
 * environment-suffixed name (`fe-recruiter-core/src/lib/crossAppSession.ts`). This module
 * is our side of that same contract:
 *
 *  - **Read:** a standalone visit with no session of its own picks the shared token up
 *    (see `adoptSharedSession` in auth-token-store), so a recruiter who is already signed
 *    in lands here signed in.
 *  - **Publish:** our own login writes the same cookies, so the recruiter app opens
 *    signed in too.
 *  - **Clear:** an explicit logout here revokes them, exactly as the recruiter's logout does.
 *
 * The contract's quirks are the other apps' parsers, not ours to fix: the value is
 * `JSON.stringify(token)` (wrapped in double quotes, not URL-encoded) and the second
 * cookie really is spelled `reruiter_portal`.
 *
 * Only THIS environment's cookies take part. Every other environment's copy is still
 * swept by `clearLegacyDomainCookies` and nginx (VA-28199), so the header-size fix holds.
 *
 * Off — every function a no-op — in the hosted bundle (the host owns the session), inside
 * any iframe (ATS embed, showcase: the parent owns it), and on any host whose environment
 * cannot be resolved (localhost, IPs), so local dev behaves exactly as before.
 */
import { hasLiveSession, unquote } from './auth-session';
import { isHostedBundle } from './hosted-bundle';
const isBrowser = () => typeof document !== 'undefined' && typeof window !== 'undefined';
/**
 * The recruiter app's `NEXT_PUBLIC_ENV` for the deployment we sit next to. Read from the
 * host so no deploy-time `.env` change is needed: `dev.interviews.vriti.ai` -> dev,
 * `sit.interviews.vriti.ai` -> sit, `interviews.vriti.ai` -> production.
 * `REACT_APP_SHARED_SESSION_ENV` overrides it for any other host layout.
 */
export const sharedSessionEnv = (host) => {
    const override = process.env.REACT_APP_SHARED_SESSION_ENV;
    if (override)
        return override;
    const h = (host || '').toLowerCase();
    const prefixed = h.match(/^(dev|sit)\.interviews\./);
    if (prefixed)
        return prefixed[1];
    if (h.startsWith('interviews.'))
        return 'production';
    return '';
};
/** `.vriti.ai` for `interviews.vriti.ai` / `sit.interviews.vriti.ai`; '' for localhost/IPs. */
const sharedDomain = (host) => {
    if (/^(localhost|127\.0\.0\.1|\[?::1\]?)$/.test(host))
        return '';
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host))
        return '';
    const match = host.match(/([^.]+\.[^.]+)$/);
    return match ? `.${match[1]}` : '';
};
/** The shared cookie names for an environment. `reruiter_portal` is misspelled on purpose. */
export const sharedSessionCookieNames = (env) => ({
    session: `vriti-${env}-access-token`,
    portal: 'reruiter_portal',
    portalType: `login-to-portal-${env}`,
});
const context = () => {
    if (!isBrowser() || isHostedBundle())
        return null;
    try {
        if (window.self !== window.top)
            return null;
    }
    catch (_a) {
        return null; // cross-origin parent: definitely framed
    }
    const host = window.location.hostname || '';
    const env = sharedSessionEnv(host);
    const domain = sharedDomain(host);
    if (!env || !domain)
        return null;
    return { domain, names: sharedSessionCookieNames(env) };
};
/**
 * Cookie names this page must NOT sweep: our own environment's shared session.
 * Empty when the shared session is off here, so the sweep behaves exactly as before.
 */
export const protectedSharedCookieKeys = () => {
    const ctx = context();
    return ctx ? [ctx.names.session, ctx.names.portal, ctx.names.portalType] : [];
};
const readRawCookie = (name) => {
    const hit = document.cookie.split('; ').find((entry) => entry.split('=')[0] === name);
    if (!hit)
        return null;
    const raw = hit.split('=').slice(1).join('=') || '';
    try {
        return decodeURIComponent(raw);
    }
    catch (_a) {
        return raw;
    }
};
const isCandidateJwt = (token) => {
    const payload = token.split('.')[1];
    if (!payload)
        return false;
    try {
        const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return (claims === null || claims === void 0 ? void 0 : claims.account_type) === 'candidate';
    }
    catch (_a) {
        return false;
    }
};
/**
 * The live organization token another vriti.ai app shared, or null.
 * A candidate's token is never a session here — the login screen refuses them too.
 */
export const readSharedAccessToken = () => {
    const ctx = context();
    if (!ctx)
        return null;
    const token = unquote(readRawCookie(ctx.names.session));
    if (!token || !hasLiveSession(token) || isCandidateJwt(token))
        return null;
    if (unquote(readRawCookie(ctx.names.portalType)) === 'candidate')
        return null;
    return token;
};
/**
 * Share a session WE just opened (our own login) with the other vriti.ai apps. Expires
 * with the token, like our own cookies, so a dead token never lingers on the domain.
 */
export const publishSharedSession = (token, accountType = 'organization') => {
    const ctx = context();
    const access = unquote(token);
    if (!ctx || !access)
        return;
    const exp = (() => {
        try {
            const claims = JSON.parse(atob((access.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')));
            return typeof (claims === null || claims === void 0 ? void 0 : claims.exp) === 'number' ? claims.exp : null;
        }
        catch (_a) {
            return null;
        }
    })();
    const ttl = exp === null ? 12 * 60 * 60 : Math.floor(exp - Date.now() / 1000);
    if (ttl <= 0)
        return;
    const attrs = `; Max-Age=${ttl}; Path=/; Domain=${ctx.domain}; SameSite=Lax; Secure`;
    const value = JSON.stringify(access);
    document.cookie = `${ctx.names.session}=${value}${attrs}`;
    document.cookie = `${ctx.names.portal}=${value}${attrs}`;
    document.cookie = `${ctx.names.portalType}=${JSON.stringify(accountType)}${attrs}`;
};
/** Explicit logout: revoke the shared session, as the recruiter app's logout does. */
export const clearSharedSession = () => {
    const ctx = context();
    if (!ctx)
        return;
    [ctx.names.session, ctx.names.portal, ctx.names.portalType].forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${ctx.domain}; SameSite=Lax; Secure`;
    });
};
